import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import AuditLogTimeline from "./AuditLogTimeline";
import ApproveConfirmModal from "./ApproveConfirmModal";
import RejectReasonModal from "./RejectReasonModal";
import { listEmployeeCompanyJoinRequestAuditLogsAction } from "../../containers/companyDashboard/action";
import {
  Box,
  Typography,
  Chip,
  Button,
  Drawer,
  TextField,
  IconButton,
  Popover,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@material-ui/core";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import AddIcon from "@material-ui/icons/Add";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";
import EyeIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import theme from "../../theme";
import CommonTable from "../table";
import CommonInput from "../input";
import { fieldValidators } from "../../validators";
import { enqueueSnackbar } from "notistack";
import CommonButton from "../button";
import { hasCap, currentUserHasMoney, MONEY_MASK } from "../../lib/permissions";
import CommonBankAccountNumber from "../commonBankAccountNumber";
import CommonBanksSelect from "../commonBanksSelect";

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles(() => ({
  root: {
    padding: 24,
    // backgroundColor: theme.colors.Porcelain,
    minHeight: "100vh",
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBLACK,
    letterSpacing: -0.4,
  },
  pageSubtitle: {
    fontSize: 13,
    color: "#8892a4",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    marginTop: 2,
  },
  countBadge: {
    background: "#ECF3FF",
    color: theme.colors.DodgerBlue,
    fontSize: 12,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    padding: "4px 14px",
    borderRadius: 20,
    border: `1.5px solid ${theme.colors.Periwinkle}`,
  },

  // Avatar
  avatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${theme.colors.DodgerBlue} 0%, #7BAEFF 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colors.White,
    fontSize: 12,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    flexShrink: 0,
  },
  nameCell: { display: "flex", alignItems: "center", gap: 10 },
  namePrimary: {
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 13.5,
    color: theme.colors.Violet,
    lineHeight: 1.3,
  },
  nameSecondary: {
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 11.5,
    color: "#8892a4",
    marginTop: 1,
  },

  // Status chips
  chipApproved: { background: "#E8FDF1", color: "#1A8C4E", fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 11.5, height: 24 },
  chipPending: { background: "#ECF3FF", color: theme.colors.DodgerBlue, fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 11.5, height: 24 },
  chipDraft: { background: theme.colors.Porcelain, color: "#8892a4", fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 11.5, height: 24 },
  chipRejected: { background: theme.colors.Cinderella, color: theme.colors.Monza, fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 11.5, height: 24 },
  valPending: { background: "#ECF3FF", color: theme.colors.DodgerBlue, border: `1px solid ${theme.colors.Periwinkle}`, fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 11, height: 22 },
  valApproved: { background: "#E8FDF1", color: "#1A8C4E", fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 11, height: 22 },
  valFailed: { background: theme.colors.Cinderella, color: theme.colors.Monza, fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 11, height: 22 },

  salary: {
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
    fontSize: 13.5,
    fontVariantNumeric: "tabular-nums",
  },

  // Row action buttons
  btnApprove: {
    background: "#16A34A",
    color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 12,
    textTransform: "none",
    borderRadius: 8,
    minWidth: 0,
    padding: "5px 12px",
    boxShadow: "0 2px 8px rgba(22,163,74,0.25)",
    "&:hover": { background: "#15803D", boxShadow: "0 4px 12px rgba(22,163,74,0.35)" },
  },
  btnReject: {
    background: "#DC2626",
    color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 12,
    textTransform: "none",
    borderRadius: 8,
    minWidth: 0,
    padding: "5px 12px",
    boxShadow: "0 2px 8px rgba(220,38,38,0.2)",
    "&:hover": { background: "#B91C1C", boxShadow: "0 4px 12px rgba(220,38,38,0.3)" },
  },
  btnEdit: {
    background: "#ECF3FF",
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 12,
    textTransform: "none",
    borderRadius: 8,
    minWidth: 0,
    padding: "5px 12px",
    "&:hover": { background: "#D6E8FF" },
  },
  btnView: {
    background: theme.colors.Porcelain,
    color: "#8892a4",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 12,
    textTransform: "none",
    borderRadius: 8,
    minWidth: 0,
    padding: "5px 12px",
    border: `1px solid ${theme.colors.Gallery}`,
    "&:hover": { background: theme.colors.Gallery, color: theme.colors.Violet },
  },
  actionsCell: { display: "flex", gap: 6, alignItems: "center" },

  // ── Drawer ────────────────────────────────────────────────────────────────
  drawerPaper: { width: 520, display: "flex", flexDirection: "column" },
  drawerHeader: {
    padding: "24px 28px 20px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    background: theme.colors.White,
    zIndex: 1,
    borderBottom: "1px solid #ECF3FF",
  },
  drawerTitle: { fontSize: 17, fontFamily: theme.fontFamily.MARKPROBLACK, color: theme.colors.Violet },
  drawerSubtitle: { fontSize: 12, fontFamily: theme.fontFamily.MARKPROBOOK, color: "#8892a4", marginTop: 3 },
  closeBtn: {
    width: 32, height: 32,
    border: `1.5px solid ${theme.colors.Gallery}`,
    background: theme.colors.Porcelain, color: "#8892a4",
    "&:hover": { background: theme.colors.Cinderella, borderColor: theme.colors.RadicalRed, color: theme.colors.Monza },
  },
  drawerBody: { padding: "24px 28px", flex: 1, overflowY: "auto" },

  // Tab strip inside the drawer — mirrors ViewSwitcher's segmented look so the
  // drawer feels consistent with the outer employees/applications toggle.
  drawerTabs: {
    display: "inline-flex",
    backgroundColor: theme.colors.Zumthor,
    borderRadius: 12,
    padding: 4,
    border: `1px solid ${theme.colors.Periwinkle}`,
    margin: "18px 28px 0",
  },
  drawerTab: {
    border: "none",
    background: "transparent",
    padding: "8px 18px",
    minWidth: 120,
    cursor: "pointer",
    borderRadius: 9,
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    fontSize: 13,
    transition: "all 160ms ease",
    "&:hover:not(:disabled)": { color: theme.colors.DodgerBlue },
    "&:focus": { outline: "none" },
  },
  drawerTabActive: {
    backgroundColor: theme.colors.White,
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    boxShadow: "0 1px 3px rgba(62, 131, 251, 0.18)",
  },
  sectionLabel: {
    fontSize: 10.5,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: theme.colors.DodgerBlue,
    marginBottom: 14,
    marginTop: 24,
    display: "flex",
    alignItems: "center",
    gap: 8,
    "&:first-child": { marginTop: 0 },
    "&::after": { content: '""', flex: 1, height: 1, background: "#ECF3FF" },
  },
  fieldGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
  fieldLabel: {
    fontSize: 11, fontFamily: theme.fontFamily.MARKPROBOLD,
    color: "#8892a4", textTransform: "uppercase", letterSpacing: 0.5,
    display: "block", marginBottom: 4,
  },
  fieldValue: { fontSize: 14, fontFamily: theme.fontFamily.MARKPROBOOK, color: theme.colors.Violet },
  fieldInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 9, fontSize: 13.5,
      fontFamily: theme.fontFamily.MARKPROBOOK,
      backgroundColor: "#FAFCFF",
      "& fieldset": { borderColor: theme.colors.Gallery, borderWidth: 1.5 },
      "&:hover fieldset": { borderColor: theme.colors.Periwinkle },
      "&.Mui-focused fieldset": { borderColor: theme.colors.DodgerBlue },
    },
  },
  editBanner: {
    background: "#ECF3FF", border: "1.5px dashed #C5DAFE", borderRadius: 10,
    padding: "10px 14px", marginBottom: 18, fontSize: 12.5,
    fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.DodgerBlue,
    display: "flex", alignItems: "center", gap: 8,
  },
  rejectionBox: {
    marginTop: 14, marginBottom: 4,
    background: theme.colors.Cinderella, borderRadius: 10,
    padding: "14px 16px", borderLeft: `3px solid ${theme.colors.RadicalRed}`,
  },
  rejectionLabel: {
    fontSize: 11, fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.Monza,
    marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5,
  },
  rejectionText: { fontSize: 13, fontFamily: theme.fontFamily.MARKPROBOOK, color: "#7A001A" },
  drawerFooter: {
    padding: "16px 28px", borderTop: "1px solid #ECF3FF",
    display: "flex", gap: 10, justifyContent: "flex-end",
    position: "sticky", bottom: 0, background: theme.colors.White,
  },
  btnApproveLg: {
    background: "#16A34A", color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 13.5,
    textTransform: "none", borderRadius: 10, padding: "10px 22px",
    boxShadow: "0 2px 10px rgba(22,163,74,0.3)",
    "&:hover": { background: "#15803D", boxShadow: "0 4px 16px rgba(22,163,74,0.4)" },
  },
  btnRejectLg: {
    background: "#DC2626", color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 13.5,
    textTransform: "none", borderRadius: 10, padding: "10px 22px",
    boxShadow: "0 2px 10px rgba(220,38,38,0.25)",
    "&:hover": { background: "#B91C1C", boxShadow: "0 4px 16px rgba(220,38,38,0.35)" },
  },
  btnSave: {
    background: theme.colors.MintGreen, color: "#0A5A2A",
    fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 13.5,
    textTransform: "none", borderRadius: 10, padding: "10px 22px",
    "&:hover": { background: "#5DE889" },
  },
  btnCancel: {
    background: theme.colors.Porcelain, color: "#8892a4",
    fontFamily: theme.fontFamily.MARKPROBOOK, fontSize: 13.5,
    textTransform: "none", borderRadius: 10, padding: "10px 22px",
    border: `1.5px solid ${theme.colors.Gallery}`,
    "&:hover": { background: theme.colors.Gallery, color: theme.colors.Violet },
  },
  btnEditLg: {
    background: "#ECF3FF", color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOLD, fontSize: 13.5,
    textTransform: "none", borderRadius: 10, padding: "10px 22px",
    "&:hover": { background: "#D6E8FF" },
  },
  countBadge: {
    background: "#ECF3FF",
    color: theme.colors.DodgerBlue,
    fontSize: 12,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    padding: "4px 14px",
    borderRadius: 20,
    border: `1.5px solid ${theme.colors.Periwinkle}`,
  },
  colManagerBtn: {
    background: theme.colors.White,
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 12,
    textTransform: "none",
    borderRadius: 10,
    border: `1.5px solid ${theme.colors.Periwinkle}`,
    padding: "6px 14px",
    display: "flex",
    alignItems: "center",
    gap: 6,
    "&:hover": { background: "#ECF3FF" },
  },
  columnRowDragging: {
    background: "#ECF3FF",
    boxShadow: "0 2px 12px rgba(62,131,251,0.15)",
    borderRadius: 8,
    opacity: 0.9,
  },
  dragHandle: { color: "#C5DAFE", fontSize: 18, flexShrink: 0 },
  columnCheckbox: {
    padding: 4,
    color: theme.colors.DodgerBlue,
    "&.Mui-checked": { color: theme.colors.DodgerBlue },
  },
  columnLabel: {
    fontSize: 13,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    flex: 1,
  },
  columnList: {
    padding: "8px 0",
    maxHeight: 280,
    overflowY: "auto",
  },
  columnRow: {
    display: "flex",
    alignItems: "center",
    padding: "4px 12px 4px 8px",
    gap: 4,
    cursor: "grab",
    "&:hover": { background: "#F8FAFF" },
    borderRadius: 8,
    margin: "1px 6px",
    transition: "background 0.12s",
  },
  popoverTitle: {
    fontSize: 13,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
  },
  popoverSubtitle: {
    fontSize: 11,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: "#8892a4",
    marginTop: 1,
  },

  popoverFooter: {
    padding: "10px 16px",
    borderTop: "1px solid #ECF3FF",
    display: "flex",
    gap: 8,
    alignItems: "center",
  },

  // Add custom column
  addColSection: {
    padding: "10px 16px",
    borderTop: "1px solid #ECF3FF",
    background: "#FAFCFF",
  },
  addColTitle: {
    fontSize: 11,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: "#8892a4",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  addColForm: { display: "flex", gap: 8, alignItems: "flex-start", flexDirection: "column" },
  addColRow: { display: "flex", gap: 8, width: "100%" },
  addColInput: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 12.5,
      fontFamily: theme.fontFamily.MARKPROBOOK,
      backgroundColor: theme.colors.White,
      "& fieldset": { borderColor: theme.colors.Gallery, borderWidth: 1.5 },
      "&:hover fieldset": { borderColor: theme.colors.Periwinkle },
      "&.Mui-focused fieldset": { borderColor: theme.colors.DodgerBlue },
    },
  },
  addColBtn: {
    background: theme.colors.DodgerBlue,
    color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 12,
    textTransform: "none",
    borderRadius: 8,
    minWidth: 0,
    padding: "7px 12px",
    whiteSpace: "nowrap",
    "&:hover": { background: "#2A6FE6" },
    "&:disabled": { background: theme.colors.Gallery, color: "#8892a4" },
  },
  resetBtn: {
    background: "transparent",
    color: "#8892a4",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 12,
    textTransform: "none",
    borderRadius: 8,
    padding: "6px 10px",
    border: `1px solid ${theme.colors.Gallery}`,
    "&:hover": { background: theme.colors.Gallery },
  },
  showAllBtn: {
    background: "#ECF3FF",
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 12,
    textTransform: "none",
    borderRadius: 8,
    padding: "6px 10px",
    "&:hover": { background: "#D6E8FF" },
  },


  // Column manager popover
  popoverPaper: {
    width: 300,
    borderRadius: 14,
    boxShadow: "0 8px 32px rgba(21,14,74,0.14)",
    overflow: "hidden",
  },
  popoverHeader: {
    padding: "14px 16px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #ECF3FF",
  },
  popoverTitle: {
    fontSize: 13,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
  },
  popoverSubtitle: {
    fontSize: 11,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: "#8892a4",
    marginTop: 1,
  },
  headerRight: { display: "flex", alignItems: "center", gap: 10 },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────
function recordValid(d) {
  return fieldValidators.validPhoneInput(d.profile?.phone) &&
    (d?.profile?.nin ? fieldValidators.validNINInput(d.profile?.nin) : true) &&
    (d?.profile?.bvn ? fieldValidators.validBVNInput(d.profile?.bvn) : true) &&
    fieldValidators.validAccountNumberInput(d.bank?.number) &&
    fieldValidators.validSalaryInput(d.profile?.salary) &&
    fieldValidators.nameValidTextInput(d?.first_name) &&
    fieldValidators.nameValidTextInput(d?.last_name) &&
    fieldValidators.validSavingsInput(d?.profile?.savings) &&
    d.bank?.name &&
    d.bank?.code && d?.bank?.account_holder
}
function getInitials(r) {
  return (
    (r.employment_data?.first_name?.[0] || "") +
    (r.employment_data?.last_name?.[0] || "")
  ).toUpperCase();
}
function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function formatSalary(s) {
  // financials.view gate: mask salary figures for users without it.
  if (!currentUserHasMoney()) return MONEY_MASK;
  return "₦" + parseInt(s || 0).toLocaleString();
}

function StateChip({ state, classes }) {
  const map = {
    DRAFT: { cls: classes.chipDraft, label: "Draft" },
    SUBMITTED: { cls: classes.chipPending, label: "Pending" },
    APPROVED: { cls: classes.chipApproved, label: "Approved" },
    REJECTED: { cls: classes.chipRejected, label: "Rejected" },
  };
  const { cls, label } = map[state] || map.DRAFT;
  return <Chip label={label} className={cls} size="small" />;
}

function ValidationChip({ status, classes }) {
  const map = {
    PENDING: { cls: classes.valPending, label: "Pending" },
    APPROVED: { cls: classes.valApproved, label: "Approved" },
    FAILED: { cls: classes.valFailed, label: "Failed" },
    SUBMITTED: { cls: classes.chipPending, label: "Pending" },
  };
  const { cls, label } = map[status] || map.PENDING;
  return <Chip label={label} className={cls} size="small" />;
}

function FieldView({ label, value, classes, fullWidth = false }) {
  return (
    <Box style={fullWidth ? { gridColumn: "1 / -1" } : {}}>
      <span className={classes.fieldLabel}>{label}</span>
      <Typography className={classes.fieldValue}>{value || "—"}</Typography>
    </Box>
  );
}

function FieldEdit({ label, value, onChange, fullWidth = false, classes, ...props }) {
  return (
    <Box style={fullWidth ? { gridColumn: "1 / -1" } : {}}>
      <span className={classes.fieldLabel}>{label}</span>
      <TextField
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        className={classes.fieldInput}
        {...props}
      />
    </Box>
  );
}

// Join-request action permissions. Company admins/HR keep their own-scope
// access; a backoffice (super or staff) user needs the matching capability.
// Supers pass via hasCap.
const getJoinRequestCaps = () => {
  let u = null;
  try {
    u = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    u = null;
  }
  const companyRole = !!u?.is_admin || !!u?.is_hr;
  return {
    mayEdit: companyRole || hasCap(u, "join_requests.edit"),
    mayApprove: companyRole || hasCap(u, "join_requests.approve"),
    mayReject: companyRole || hasCap(u, "join_requests.reject"),
  };
};

// ─── Detail Drawer ────────────────────────────────────────────────────────────
function ApplicationDrawer({
  record,
  onClose,
  onApprove,
  onReject,
  onSave,
  classes,
  saveLoading = false,
  approveLoading = false,
  rejectLoading = false
}) {
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [tab, setTab] = useState("details");
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const dispatch = useDispatch();
  const auditLogsSlice = useSelector(
    (s) => s?.companyDashboardReducer?.joinRequestAuditLogs?.[record?.id]
  );
  const auditLogsLoading = useSelector(
    (s) => s?.companyDashboardReducer?.joinRequestAuditLogsLoading &&
           s?.companyDashboardReducer?.joinRequestAuditLogsForId === record?.id
  );

  // The backend paginates audit logs — accept either `results` (DRF default) or
  // `data` (our other paginated endpoints) so the caller doesn't have to care.
  const auditEntries =
    auditLogsSlice?.results || auditLogsSlice?.data || [];

  // Lazy-load the audit log the first time the user switches to the tab per
  // record — avoids paying the roundtrip for the far more common Details view.
  useEffect(() => {
    if (record?.id && tab === "audit" && !auditLogsSlice) {
      dispatch(listEmployeeCompanyJoinRequestAuditLogsAction(record.id, { page_size: 20 }));
    }
  }, [record?.id, tab, auditLogsSlice, dispatch]);

  // Reset to Details whenever the drawer opens a new record — otherwise
  // "Audit" stays selected but points at the previous record's data for a
  // beat until the useEffect refires.
  useEffect(() => {
    setTab("details");
  }, [record?.id]);

  const startEdit = () => { setEditData(JSON.parse(JSON.stringify(record.employment_data))); setEditMode(true); };
  const cancelEdit = () => { setEditMode(false); setEditData(null); };
  const saveEdit = () => { onSave(record.id, { ...(record?.employment_data), ...editData }); setEditMode(false); setEditData(null); };

  const setField = (path, value) => {
    const keys = path.split(".");
    setEditData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {

        if (obj[keys[i]] == null) {
          obj[keys[i]] = {};
        }
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const d = editMode ? editData : record.employment_data;
  const canAct = !record.is_approved && !record.is_rejected && !editMode && record.state === "SUBMITTED";
  const { mayEdit, mayApprove, mayReject } = getJoinRequestCaps();

  const fp = (label, path, val, full = false, props = {}) => {
    return editMode ? (
      <FieldEdit
        label={label}
        value={val}
        onChange={(v) => setField(path, v)}
        fullWidth={full}
        classes={classes}
        {...props}
      />
    ) : (
      <FieldView label={label} value={val} fullWidth={full} classes={classes} {...props} />
    );
  }


  const canEdit = (!record?.approved_at && !record?.rejected_at);
  const canSave = recordValid(d) && (JSON.stringify(d) !== JSON.stringify(record.employment_data));

  const employeeFullName = [
    record?.employment_data?.first_name,
    record?.employment_data?.last_name,
  ].filter(Boolean).join(" ").trim();

  return (
    <Drawer anchor="right" open={!!record} onClose={onClose} classes={{ paper: classes.drawerPaper }}>
      <Box className={classes.drawerHeader}>
        <Box>
          <Typography className={classes.drawerTitle}>{d.first_name} {d.last_name}</Typography>
          <Typography className={classes.drawerSubtitle}>{d.employee_id} · {record.employment_data.profile?.position}</Typography>
        </Box>
        <IconButton className={classes.closeBtn} onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <div className={classes.drawerTabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "details"}
          className={`${classes.drawerTab} ${tab === "details" ? classes.drawerTabActive : ""}`}
          onClick={() => setTab("details")}
        >
          Details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "audit"}
          className={`${classes.drawerTab} ${tab === "audit" ? classes.drawerTabActive : ""}`}
          onClick={() => setTab("audit")}
        >
          Audit log
        </button>
      </div>

      {tab === "audit" ? (
        <Box className={classes.drawerBody}>
          <AuditLogTimeline
            entries={auditEntries}
            loading={auditLogsLoading}
            employeeName={employeeFullName}
          />
        </Box>
      ) : (
      <Box className={classes.drawerBody}>
        {editMode && <Box className={classes.editBanner}>✏ Edit Mode — modify fields and save</Box>}

        {record.is_rejected && record.rejection_reason && (
          <Box className={classes.rejectionBox}>
            <Typography className={classes.rejectionLabel}>Rejection Reason</Typography>
            <Typography className={classes.rejectionText}>{record.rejection_reason}</Typography>
          </Box>
        )}

        <Typography className={classes.sectionLabel}>Status</Typography>
        <Box className={classes.fieldGrid} style={{ marginBottom: 4 }}>
          <Box><span className={classes.fieldLabel}>State</span><StateChip state={record.state} classes={classes} /></Box>
          <Box><span className={classes.fieldLabel}>Validation</span><ValidationChip status={record.validation_status} classes={classes} /></Box>
        </Box>

        <Typography className={classes.sectionLabel}>Personal Information</Typography>
        <Box className={classes.fieldGrid}>
          {fp("First Name", "first_name", d.first_name, false, {
            error: editMode && !fieldValidators.nameValidTextInput(d.first_name),
            helperText: editMode && fieldValidators.nameValidTextHelperText(d.first_name)
          })}
          {fp("Last Name", "last_name", d.last_name, false, {
            error: editMode && !fieldValidators.nameValidTextInput(d.last_name),
            helperText: editMode && fieldValidators.nameValidTextHelperText(d.last_name)
          })}
          {fp("Email", "email", d.email, false)}
          {fp("Employee ID", "employee_id", d.employee_id, false)}
          {fp("Gender", "gender", d.gender === "M" ? "Male" : "Female")}
          {fp("Withdrawal %", "withdrawal_percent", editMode ? d.withdrawal_percent : d.withdrawal_percent + "%")}
        </Box>

        <Typography className={classes.sectionLabel}>Role & Branch</Typography>
        <Box className={classes.fieldGrid}>
          {fp(
            "Position",
            "profile.position",
            d.profile?.position
          )}
          {fp("Office Location", "branch", d.branch)}
          {fp("Phone", "profile.phone", d.profile?.phone, false, {
            error: editMode && !fieldValidators.validPhoneInput(d.profile?.phone),
            helperText: editMode && fieldValidators.phoneHelperText(d.profile?.phone)
          })}
          {fp("Monthly Salary", "profile.salary", editMode ? d.profile?.salary : formatSalary(d.profile?.salary), false, {
            error: editMode && !fieldValidators.validSalaryInput(d.profile?.salary),
            helperText: editMode && fieldValidators.salaryHelperText(d.profile?.salary)
          })}
          {
            fp("Savings", "profile.savings", editMode ? d.profile?.savings : d?.profile?.savings, false, {
              error: editMode && !fieldValidators.validSavingsInput(d.profile?.savings),
              helperText: editMode && fieldValidators.savingsHelperText(d.profile?.savings)
            })
          }
        </Box>

        <Typography className={classes.sectionLabel}>Identity & Compliance</Typography>
        <Box className={classes.fieldGrid}>
          {fp("NIN", "profile.nin", d.profile?.nin, false, {
            error: editMode && !fieldValidators.validNINInput(d.profile?.nin),
            helperText: editMode && fieldValidators.ninHelperText(d.profile?.nin)
          })}
          {fp("BVN", "profile.bvn", d.profile?.bvn, false, {
            error: editMode && !fieldValidators.validBVNInput(d.profile?.bvn),
            helperText: editMode && fieldValidators.bvnHelperText(d.profile?.bvn)
          })}
        </Box>

        <Typography className={classes.sectionLabel}>Bank Details</Typography>
        <Box className={classes.fieldGrid}>

          {editMode && <><CommonBanksSelect value={d.bank?.name && d.bank?.code ? {
            label: d.bank?.name, value: d.bank?.code
          } : { label: "", value: "" }} onChange={(data) => {
            if (data) {
              setField("bank.code", data.value);
              setField("bank.name", data.label);
            } else {
              setField("bank.code", null);
              setField("bank.name", null);
            }
          }}
            className={classes.fieldInput}
          />
            <CommonBankAccountNumber value={d.bank?.number && d.bank?.code && d.bank?.account_holder ? {
              account_number: d.bank?.number,
              bank_code: d.bank?.code,
              account_name: d.bank?.account_holder
            } : { account_number: null, bank_code: null, account_name: null }} onChange={(data) => {
              setField("bank.number", data?.account_number);
              setField("bank.account_holder", data?.account_name);
            }} className={classes.fieldInput}
              allowChange={editMode} />
          </>}
          {!editMode && <>
            {fp("Bank Name", "bank.name", d.bank?.name)}
            {fp("Account Number", "bank.number", d.bank?.number, false, {
              error: editMode && !fieldValidators.validAccountNumberInput(d.bank?.number),
              helperText: editMode && fieldValidators.accountNumberHelperText(d.bank?.number)
            })}
            {fp("Account Holder", "bank.account_holder", d.bank?.account_holder, true)}
          </>}
        </Box>
      </Box>
      )}

      <Box className={classes.drawerFooter}>
        {editMode ? (
          <>
            <Button className={classes.btnCancel} onClick={cancelEdit}>Cancel</Button>
            <CommonButton
              text={"Save Changes"}
              className={classes.btnSave}
              onClick={saveEdit}
              startIcon={<CheckIcon />}
              disabled={!canSave}
              loading={saveLoading}
            />
          </>
        ) : (
          <>
            {
              canEdit && mayEdit && (
                <CommonButton text={'Edit'} className={classes.btnEditLg} startIcon={<EditIcon />} onClick={startEdit} loading={saveLoading} />
              )
            }

            {canAct && (mayApprove || mayReject) && (
              <>
                {mayReject && (
                <CommonButton
                  text={'Reject'}
                  className={classes.btnRejectLg}
                  startIcon={<ClearIcon />}
                  // Open the confirmation modal instead of firing with a
                  // hardcoded reason — spec requires a real message that
                  // reaches the employee.
                  onClick={() => setRejectOpen(true)}
                  loading={rejectLoading}
                />
                )}
                {mayApprove && (
                <CommonButton
                  text={'Approve'}
                  className={classes.btnApproveLg}
                  startIcon={<CheckIcon />}
                  // Client-side field validity is still gated here so ops don't
                  // even reach the confirmation modal for a broken record.
                  onClick={() => {
                    if (recordValid(record?.employment_data)) {
                      setApproveOpen(true);
                    } else {
                      enqueueSnackbar(
                        "Cannot approve: record has invalid/unfilled fields. Please edit and fix errors before approving.",
                        { variant: "error" }
                      );
                    }
                  }}
                  loading={approveLoading}
                />
                )}
              </>
            )}
          </>
        )}
      </Box>

      <ApproveConfirmModal
        open={approveOpen}
        employeeName={employeeFullName}
        validationStatus={record?.validation_status}
        loading={approveLoading}
        onCancel={() => setApproveOpen(false)}
        onConfirm={() => {
          onApprove(record.id);
          setApproveOpen(false);
        }}
      />

      <RejectReasonModal
        open={rejectOpen}
        employeeName={employeeFullName}
        loading={rejectLoading}
        onCancel={() => setRejectOpen(false)}
        onConfirm={(reason) => {
          onReject(record.id, reason);
          setRejectOpen(false);
        }}
      />
    </Drawer>
  );
}

// ─── Column Manager Popover ───────────────────────────────────────────────────
function ColumnManager({ anchorEl, onClose, columnDefs, onToggle, onReorder, onAddColumn, onReset, classes }) {
  const open = Boolean(anchorEl);
  const [newColHeader, setNewColHeader] = useState("");
  const [newColAccessor, setNewColAccessor] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver = (e, i) => { e.preventDefault(); setDragOverIdx(i); };
  const handleDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setDragOverIdx(null); return; }
    onReorder(dragIdx, i);
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const handleAdd = () => {
    if (!newColHeader.trim() || !newColAccessor.trim()) return;
    onAddColumn({ Header: newColHeader.trim(), accessor: newColAccessor.trim(), isCustom: true });
    setNewColHeader("");
    setNewColAccessor("");
  };

  const visibleCount = columnDefs.filter((c) => c.visible).length;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      classes={{ paper: classes.popoverPaper }}
    >
      {/* Header */}
      <Box className={classes.popoverHeader}>
        <Box>
          <Typography className={classes.popoverTitle}>Manage Columns</Typography>
          <Typography className={classes.popoverSubtitle}>{visibleCount} of {columnDefs.length} visible · drag to reorder</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* Column toggle + drag list */}
      <Box className={classes.columnList}>
        {columnDefs.map((col, i) => (
          <Box
            key={col.id}
            className={`${classes.columnRow} ${dragOverIdx === i ? classes.columnRowDragging : ""}`}
            draggable
            onDragStart={() => handleDragStart(i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDrop={() => handleDrop(i)}
            onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
          >
            <DragIndicatorIcon className={classes.dragHandle} />
            <FormControlLabel
              style={{ flex: 1, margin: 0 }}
              control={
                <Checkbox
                  checked={col.visible}
                  onChange={() => onToggle(col.id)}
                  className={classes.columnCheckbox}
                  size="small"
                  // Always keep Actions visible
                  disabled={col.id === "actions"}
                />
              }
              label={<span className={classes.columnLabel}>{col.Header}</span>}
            />
            {col.isCustom && (
              <IconButton size="small" onClick={() => onToggle(col.id, true)} style={{ padding: 2, color: "#FF3B59" }}>
                <CloseIcon style={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      <Divider />

      {/* Footer actions */}
      <Box className={classes.popoverFooter}>
        <Button className={classes.showAllBtn} onClick={() => columnDefs.forEach((c) => !c.visible && onToggle(c.id))}>
          Show All
        </Button>
        <Button className={classes.resetBtn} onClick={onReset}>Reset</Button>
      </Box>

      {/* Add custom column */}
      {/* <Box className={classes.addColSection}>
        <Typography className={classes.addColTitle}>Add Custom Column</Typography>
        <Box className={classes.addColForm}>
          <Box className={classes.addColRow}>
            <TextField
              placeholder="Header label"
              value={newColHeader}
              onChange={(e) => setNewColHeader(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.addColInput}
            />
            <TextField
              placeholder="Data key (e.g. state)"
              value={newColAccessor}
              onChange={(e) => setNewColAccessor(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.addColInput}
            />
          </Box>
          <Button
            className={classes.addColBtn}
            startIcon={<AddIcon fontSize="small" />}
            onClick={handleAdd}
            disabled={!newColHeader.trim() || !newColAccessor.trim()}
            fullWidth
          >
            Add Column
          </Button>
        </Box>
      </Box> */}
    </Popover>
  );
}


const DEFAULT_COLUMN_DEFS = [
  { id: "employee", Header: "Employee", accessor: "employment_data", visible: true, locked: false },
  { id: "position", Header: "Position", accessor: "employment_data", visible: true, locked: false },
  { id: "branch", Header: "Branch", accessor: "employment_data", visible: true, locked: false },
  { id: "salary", Header: "Salary", accessor: "employment_data", visible: true, locked: false },
  { id: "status", Header: "Status", accessor: "state", visible: true, locked: false },
  { id: "validation", Header: "Validation", accessor: "validation_status", visible: true, locked: false },
  { id: "date", Header: "Date", accessor: "submitted_at", visible: true, locked: false },
  { id: "actions", Header: "Actions", accessor: "id", visible: true, locked: true },

  // ── Bank sub-fields (hidden by default, user can toggle on) ──
  { id: "bank_name", Header: "Bank Name", accessor: "employment_data", visible: false, locked: false },
  { id: "bank_number", Header: "Account Number", accessor: "employment_data", visible: false, locked: false },
  { id: "bank_holder", Header: "Account Holder", accessor: "employment_data", visible: false, locked: false },

  // ── Identity sub-fields ──
  { id: "nin", Header: "NIN", accessor: "employment_data", visible: false, locked: false },
  { id: "bvn", Header: "BVN", accessor: "employment_data", visible: false, locked: false },

  // ── Profile sub-fields ──
  { id: "phone", Header: "Phone", accessor: "employment_data", visible: false, locked: false },
  { id: "gender", Header: "Gender", accessor: "employment_data", visible: false, locked: false },
  { id: "withdrawal", Header: "Withdrawal %", accessor: "employment_data", visible: false, locked: false },
  { id: "savings", Header: "Savings", accessor: "employment_data", visible: false, locked: false },

];


// ─── Main Component ───────────────────────────────────────────────────────────
/**
 * EmploymentApplicationsList
 *
 * Props:
 *   data                {Array}     Application records
 *   loading             {boolean}   Passed to CommonTable → shows ThreeDots spinner
 *   total               {number}    Total record count for badge
 *   onApprove           {(id) => void}
 *   onReject            {(id, reason) => void}
 *   onSaveEdit          {(id, updatedEmploymentData) => void}
 *   paginationComponent {ReactNode} Slot: render your Pagination component here
 */
export default function EmploymentApplicationsList({
  data = [],
  loading = false,
  total = 0,
  onApprove = () => { },
  onReject = () => { },
  onSaveEdit = () => { },
  saveLoading = false,
  paginationComponent = null,
  closeDrawerOnsave = false,
  approveLoading = false,
  rejectLoading = false,
}) {
  const classes = useStyles();
  const [drawerRecord, setDrawerRecord] = useState(null);
  const [colManagerAnchor, setColManagerAnchor] = useState(null);
  const [columnDefs, setColumnDefs] = useState(DEFAULT_COLUMN_DEFS);


  React.useEffect(() => {
    // If data changes while drawer is open, update the drawer record with latest data for that record
    if (drawerRecord) {
      const updatedRecord = data.find((r) => r.id === drawerRecord.id);
      if (updatedRecord) {
        setDrawerRecord(updatedRecord);
      }
    }
  }, [data])

  const handleApprove = (id) => { onApprove(id); setDrawerRecord(null); };
  const handleReject = (id, reason) => { onReject(id, reason); setDrawerRecord(null); };

  const handleToggleColumn = (id, remove = false) => {
    setColumnDefs((prev) =>
      remove
        ? prev.filter((c) => c.id !== id)
        : prev.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c))
    );
  };

  // Drag-to-reorder
  const handleReorder = (fromIdx, toIdx) => {
    setColumnDefs((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  // Add a brand-new custom column
  const handleAddColumn = (colDef) => {
    setColumnDefs((prev) => {
      // Insert before the locked Actions column
      const actionsIdx = prev.findIndex((c) => c.id === "actions");
      const next = [...prev];
      next.splice(actionsIdx, 0, { ...colDef, id: `custom_${Date.now()}`, visible: true, isCustom: true });
      return next;
    });
  };

  // Reset to default
  const handleReset = () => setColumnDefs(DEFAULT_COLUMN_DEFS);



  const columns = React.useMemo(() => {
    const cellMap = {
      employee: (value, row) => (
        <Box className={classes.nameCell}>
          <Box className={classes.avatar}>{getInitials(row)}</Box>
          <Box>
            <Typography className={classes.namePrimary}>{value?.first_name} {value?.last_name}</Typography>
            <Typography className={classes.nameSecondary}>{value?.employee_id}</Typography>
          </Box>
        </Box>
      ),
      position: (value) => value?.profile?.position || "—",
      branch: (value) => value?.branch || "—",
      salary: (value) => <span className={classes.salary}>{formatSalary(value?.profile?.salary)}</span>,
      status: (value) => <StateChip state={value} classes={classes} />,
      validation: (value) => <ValidationChip status={value} classes={classes} />,
      date: (value) => formatDate(value),
      bank_name: (value) => value?.bank?.name || "—",
      bank_number: (value) => value?.bank?.number || "—",
      bank_holder: (value) => value?.bank?.account_holder || "—",

      // Identity
      nin: (value) => value?.profile?.nin || "—",
      bvn: (value) => value?.profile?.bvn || "—",

      // Profile
      phone: (value) => value?.profile?.phone || "—",
      gender: (value) => value?.gender === "M" ? "Male" : value?.gender === "F" ? "Female" : "—",
      withdrawal: (value) => value?.withdrawal_percent ? `${value.withdrawal_percent}%` : "—",
      savings: (value) => value?.profile?.savings ? formatSalary(value.profile.savings) : "—",
      actions: (id, row) => {
        const canEdit = (!row?.approved_at && !row?.rejected_at);
        const { mayEdit } = getJoinRequestCaps();
        return (
          <Box className={classes.actionsCell} onClick={(e) => e.stopPropagation()}>
            {canEdit && mayEdit ? (
              <Button className={classes.btnEdit} size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => setDrawerRecord(row)}>
                Edit
              </Button>
            ) : (
              <Button className={classes.btnView} size="small" onClick={() => setDrawerRecord(row)}>
                View
              </Button>
            )}
          </Box>
        );
      }
    };

    return columnDefs
      .filter((c) => c.visible)
      .map((c) => ({
        Header: c.Header,
        accessor: c.accessor,
        // Custom columns: just render the raw value from the accessor key
        Cell: cellMap[c.id] || ((value) => (value !== undefined && value !== null ? String(value) : "—")),
      }));
  }, [columnDefs, classes]);


  return (
    <Box className={classes.root}>
      {/* Header */}
      {/* <Box className={classes.pageHeader}>
        <Box>
          <Typography className={classes.pageTitle}>Employee Applications</Typography>
          <Typography className={classes.pageSubtitle}>Review, approve or reject employment applications</Typography>
        </Box>
        <span className={classes.countBadge}>{total} Applications</span>
      </Box> */}

      <Box className={classes.headerRight}>
        {/* Column manager trigger */}
        <Button
          className={classes.colManagerBtn}
          startIcon={<ViewColumnIcon fontSize="small" />}
          onClick={(e) => setColManagerAnchor(e.currentTarget)}
        >
          Columns ({columnDefs.filter((c) => c.visible).length}/{columnDefs.length})
        </Button>
        <span className={classes.countBadge}>{total} Applications</span>
      </Box>



      {/* CommonTable handles loading (ThreeDots), empty state, and row rendering */}

      <CommonTable
        columns={columns}
        data={data}
        loading={loading}
        onRowClick={(rowIndex) => setDrawerRecord(data[rowIndex])}
        fullRadius
      />

      {/* Pagination slot */}
      {
        paginationComponent && (
          <Box mt={2} display="flex" justifyContent="flex-end">
            {paginationComponent}
          </Box>
        )
      }

      <ColumnManager
        anchorEl={colManagerAnchor}
        onClose={() => setColManagerAnchor(null)}
        columnDefs={columnDefs}
        onToggle={handleToggleColumn}
        onReorder={handleReorder}
        onAddColumn={handleAddColumn}
        onReset={handleReset}
        classes={classes}
      />


      {/* Detail drawer */}
      {
        drawerRecord && (
          <ApplicationDrawer
            record={drawerRecord}
            onClose={() => setDrawerRecord(null)}
            onApprove={handleApprove}
            onReject={handleReject}
            onSave={(id, updatedData) => { onSaveEdit(id, updatedData); if (closeDrawerOnsave) setDrawerRecord(null); }}
            classes={classes}
            saveLoading={saveLoading}
            approveLoading={approveLoading}
            rejectLoading={rejectLoading}
          />
        )
      }
    </Box >
  );
}
